/*
 * PKI.Keys — private/public key parsing, conversion and decryption.
 *
 * Five container formats describe the same handful of keys, and which one a
 * user has is an accident of the tool that made it:
 *   PKCS#1  "-----BEGIN RSA PRIVATE KEY-----"        (openssl genrsa, old nginx)
 *   SEC1    "-----BEGIN EC PRIVATE KEY-----"         (openssl ecparam)
 *   PKCS#8  "-----BEGIN PRIVATE KEY-----"            (modern default, Java, Go)
 *   encrypted PKCS#8 "-----BEGIN ENCRYPTED PRIVATE KEY-----"
 *   legacy encrypted PEM  (Proc-Type: 4,ENCRYPTED — an openssl-only convention)
 * Everything here normalises to PKCS#8 internally and converts back on demand,
 * so the tools never grow per-format branches.
 */
(function (root) {
    'use strict';

    var P = (root && root.PKI) || {};
    var Asn1 = P.Asn1 || (typeof require !== 'undefined' ? require('./asn1.js') : null);
    var Oid = P.Oid || (typeof require !== 'undefined' ? require('./oid.js') : null);
    var Ciphers = P.Ciphers || (typeof require !== 'undefined' ? require('./ciphers.js') : null);

    var OID_RSA = '1.2.840.113549.1.1.1';
    var OID_EC = '1.2.840.10045.2.1';
    var OID_DSA = '1.2.840.10040.4.1';
    var OID_ED25519 = '1.3.101.112';
    var OID_ED448 = '1.3.101.113';
    var OID_X25519 = '1.3.101.110';

    function subtle() {
        var c = (root && root.crypto) || (typeof globalThis !== 'undefined' ? globalThis.crypto : null);
        if (!c || !c.subtle) {
            throw new Error('WEBCRYPTO_UNAVAILABLE');
        }
        return c.subtle;
    }

    function randomBytes(n) {
        var c = (root && root.crypto) || (typeof globalThis !== 'undefined' ? globalThis.crypto : null);
        var out = new Uint8Array(n);
        c.getRandomValues(out);
        return out;
    }

    /** Bit length of a big-endian magnitude — how key size is actually defined. */
    function bitLength(bytes) {
        var b = Asn1.u8(bytes), i = 0;
        while (i < b.length && b[i] === 0) i++;
        if (i === b.length) return 0;
        var bits = (b.length - i - 1) * 8, top = b[i];
        while (top) { bits++; top >>= 1; }
        return bits;
    }

    /* ═══ AlgorithmIdentifier ═══════════════════════════════════════ */

    function readAlgId(node) {
        if (!node || !node.children || !node.children.length) return null;
        return {
            oid: node.children[0].oid,
            label: Oid.label(node.children[0].oid),
            params: node.children[1] || null
        };
    }

    /* ═══ public keys ═══════════════════════════════════════════════ */

    /**
     * Parse a SubjectPublicKeyInfo. Also accepts a bare PKCS#1 RSAPublicKey
     * ("-----BEGIN RSA PUBLIC KEY-----"), which some Java and PHP code emits.
     */
    function parsePublic(der) {
        var bytes = Asn1.u8(der);
        var node = Asn1.parse(bytes);
        if (node.tag !== Asn1.TAG.SEQUENCE) throw new Error('PUBKEY_NOT_SEQUENCE');
        var kids = node.children || [];

        // Bare PKCS#1 RSAPublicKey { modulus, exponent }
        if (kids.length === 2 && kids[0].tag === Asn1.TAG.INTEGER && kids[1].tag === Asn1.TAG.INTEGER) {
            var spki = Asn1.sequence(
                Asn1.sequence(Asn1.oid(OID_RSA), Asn1.nullValue()),
                Asn1.bitString(node.full, 0)
            );
            return finishPublic(spki, OID_RSA, null, kids[0].bytes, kids[1].bytes, null);
        }

        if (kids.length !== 2 || kids[1].tag !== Asn1.TAG.BIT_STRING) throw new Error('PUBKEY_NOT_SPKI');
        var alg = readAlgId(kids[0]);
        if (!alg) throw new Error('PUBKEY_NO_ALGID');
        var keyBits = kids[1].bits;

        if (alg.oid === OID_RSA || alg.oid === '1.2.840.113549.1.1.10') {
            var inner = Asn1.parse(keyBits);
            if (!inner.children || inner.children.length < 2) throw new Error('PUBKEY_BAD_RSA');
            return finishPublic(bytes, alg.oid, null, inner.children[0].bytes, inner.children[1].bytes, null);
        }
        if (alg.oid === OID_EC) {
            var curveOid = alg.params && alg.params.oid ? alg.params.oid : null;
            return finishPublic(bytes, alg.oid, curveOid, null, null, keyBits);
        }
        // Edwards / Montgomery keys carry the raw point with no wrapper.
        return finishPublic(bytes, alg.oid, null, null, null, keyBits);
    }

    function finishPublic(spki, algOid, curveOid, modulus, exponent, point) {
        var info = Oid.PUBKEY[algOid] || { label: Oid.label(algOid), kind: 'unknown' };
        var out = {
            kind: info.kind,
            algorithm: info.label,
            algorithmOid: algOid,
            spki: Asn1.u8(spki),
            bits: null,
            curve: null,
            curveOid: curveOid || null,
            rsa: null,
            point: point || null
        };
        if (modulus) {
            out.bits = bitLength(modulus);
            out.rsa = {
                modulus: Asn1.u8(modulus),
                exponent: Asn1.u8(exponent),
                exponentValue: exponentToNumber(exponent)
            };
        } else if (curveOid) {
            var c = Oid.CURVE[curveOid];
            out.curve = c ? c.label : curveOid;
            out.bits = c ? c.bits : null;
            out.webcryptoCurve = c ? c.webcrypto : null;
        } else if (algOid === OID_ED25519 || algOid === OID_X25519) {
            out.bits = 255; out.curve = 'Curve25519';
        } else if (algOid === OID_ED448) {
            out.bits = 448; out.curve = 'Curve448';
        }
        return out;
    }

    function exponentToNumber(bytes) {
        var b = Asn1.u8(bytes), v = 0;
        for (var i = 0; i < b.length; i++) {
            v = v * 256 + b[i];
            if (!Number.isSafeInteger(v)) return null;
        }
        return v;
    }

    /* ═══ private keys ══════════════════════════════════════════════ */

    /**
     * Parse any unencrypted private key container. Returns a normalised object
     * carrying `pkcs8` so downstream code never branches on the input format.
     */
    function parsePrivate(der) {
        var bytes = Asn1.u8(der);
        var node = Asn1.parse(bytes);
        if (node.tag !== Asn1.TAG.SEQUENCE || !node.children) throw new Error('PRIVKEY_NOT_SEQUENCE');
        var kids = node.children;

        // PKCS#1 RSAPrivateKey: 9+ INTEGERs, version first.
        if (kids.length >= 9 && kids.every(function (k) { return k.tag === Asn1.TAG.INTEGER; })) {
            return rsaFromPkcs1(bytes, node);
        }

        // SEC1 ECPrivateKey: INTEGER 1, OCTET STRING, [0] curve, [1] pubkey
        if (kids.length >= 2 && kids[0].tag === Asn1.TAG.INTEGER &&
            kids[0].bytes.length === 1 && kids[0].bytes[0] === 1 &&
            kids[1].tag === Asn1.TAG.OCTET_STRING) {
            return ecFromSec1(bytes, node);
        }

        // PKCS#8 PrivateKeyInfo: INTEGER 0, AlgorithmIdentifier, OCTET STRING
        if (kids.length >= 3 && kids[0].tag === Asn1.TAG.INTEGER &&
            kids[1].tag === Asn1.TAG.SEQUENCE && kids[2].tag === Asn1.TAG.OCTET_STRING) {
            return fromPkcs8(bytes, node);
        }

        // EncryptedPrivateKeyInfo — caller must decrypt first; say so precisely.
        if (kids.length === 2 && kids[0].tag === Asn1.TAG.SEQUENCE &&
            kids[1].tag === Asn1.TAG.OCTET_STRING) {
            var err = new Error('PRIVKEY_ENCRYPTED');
            err.encrypted = true;
            err.algorithm = Oid.label(Asn1.get(node, 0, 0) && Asn1.get(node, 0, 0).oid);
            throw err;
        }

        throw new Error('PRIVKEY_UNRECOGNISED');
    }

    function rsaFromPkcs1(bytes, node) {
        var k = node.children;
        var modulus = k[1].bytes, publicExponent = k[2].bytes;
        var pkcs8 = Asn1.sequence(
            Asn1.integerFromNumber(0),
            Asn1.sequence(Asn1.oid(OID_RSA), Asn1.nullValue()),
            Asn1.octetString(bytes)
        );
        return {
            kind: 'RSA',
            algorithm: 'RSA',
            algorithmOid: OID_RSA,
            format: 'pkcs1',
            bits: bitLength(modulus),
            curve: null,
            pkcs8: pkcs8,
            pkcs1: Asn1.u8(bytes),
            rsa: {
                modulus: Asn1.u8(modulus),
                exponent: Asn1.u8(publicExponent),
                exponentValue: exponentToNumber(publicExponent),
                primes: k.length >= 6 ? [Asn1.u8(k[4].bytes), Asn1.u8(k[5].bytes)] : null
            },
            spki: rsaSpki(modulus, publicExponent)
        };
    }

    function rsaSpki(modulus, exponent) {
        return Asn1.sequence(
            Asn1.sequence(Asn1.oid(OID_RSA), Asn1.nullValue()),
            Asn1.bitString(Asn1.sequence(Asn1.integer(modulus), Asn1.integer(exponent)), 0)
        );
    }

    function ecFromSec1(bytes, node) {
        var curveNode = Asn1.contextChild(node, 0);
        var pubNode = Asn1.contextChild(node, 1);
        var curveOid = null;
        if (curveNode && curveNode.children && curveNode.children[0]) curveOid = curveNode.children[0].oid;
        var point = null;
        if (pubNode && pubNode.children && pubNode.children[0] && pubNode.children[0].tag === Asn1.TAG.BIT_STRING) {
            point = pubNode.children[0].bits;
        }
        var c = curveOid ? Oid.CURVE[curveOid] : null;

        // RFC 5915: inside PKCS#8 the curve lives in the AlgorithmIdentifier and
        // the inner structure omits [0]. openssl pkcs8 -topk8 strips it, so we
        // strip it too — byte-identical output is what makes diffs meaningful.
        var innerParts = [Asn1.integerFromNumber(1), Asn1.octetString(node.children[1].bytes)];
        if (pubNode) innerParts.push(pubNode.full);
        var inner = Asn1.sequence(innerParts);

        var pkcs8 = Asn1.sequence(
            Asn1.integerFromNumber(0),
            Asn1.sequence(Asn1.oid(OID_EC), curveOid ? Asn1.oid(curveOid) : Asn1.nullValue()),
            Asn1.octetString(inner)
        );
        return {
            kind: 'EC',
            algorithm: 'EC',
            algorithmOid: OID_EC,
            format: 'sec1',
            bits: c ? c.bits : null,
            curve: c ? c.label : curveOid,
            curveOid: curveOid,
            webcryptoCurve: c ? c.webcrypto : null,
            pkcs8: pkcs8,
            sec1: Asn1.u8(bytes),
            point: point,
            spki: point && curveOid ? ecSpki(curveOid, point) : null
        };
    }

    function ecSpki(curveOid, point) {
        return Asn1.sequence(
            Asn1.sequence(Asn1.oid(OID_EC), Asn1.oid(curveOid)),
            Asn1.bitString(point, 0)
        );
    }

    function fromPkcs8(bytes, node) {
        var alg = readAlgId(node.children[1]);
        var inner = node.children[2].bytes;

        if (alg.oid === OID_RSA || alg.oid === '1.2.840.113549.1.1.10') {
            var rsaNode = Asn1.parse(inner);
            var k = rsaNode.children || [];
            if (k.length < 3) throw new Error('PRIVKEY_BAD_RSA');
            return {
                kind: 'RSA', algorithm: 'RSA', algorithmOid: alg.oid, format: 'pkcs8',
                bits: bitLength(k[1].bytes), curve: null,
                pkcs8: Asn1.u8(bytes), pkcs1: Asn1.u8(inner),
                rsa: {
                    modulus: Asn1.u8(k[1].bytes),
                    exponent: Asn1.u8(k[2].bytes),
                    exponentValue: exponentToNumber(k[2].bytes),
                    primes: k.length >= 6 ? [Asn1.u8(k[4].bytes), Asn1.u8(k[5].bytes)] : null
                },
                spki: rsaSpki(k[1].bytes, k[2].bytes)
            };
        }

        if (alg.oid === OID_EC) {
            var curveOid = alg.params && alg.params.oid ? alg.params.oid : null;
            var ecNode = Asn1.parse(inner);
            var pubNode = Asn1.contextChild(ecNode, 1);
            var point = null;
            if (pubNode && pubNode.children && pubNode.children[0]) point = pubNode.children[0].bits;
            var c = curveOid ? Oid.CURVE[curveOid] : null;

            // Rebuild a standalone SEC1 blob: it needs the curve back in [0],
            // which PKCS#8 moved out to the AlgorithmIdentifier.
            var sec1Parts = [Asn1.integerFromNumber(1), Asn1.octetString(ecNode.children[1].bytes)];
            if (curveOid) sec1Parts.push(Asn1.explicit(0, Asn1.oid(curveOid)));
            if (pubNode) sec1Parts.push(pubNode.full);

            return {
                kind: 'EC', algorithm: 'EC', algorithmOid: alg.oid, format: 'pkcs8',
                bits: c ? c.bits : null, curve: c ? c.label : curveOid, curveOid: curveOid,
                webcryptoCurve: c ? c.webcrypto : null,
                pkcs8: Asn1.u8(bytes), sec1: Asn1.sequence(sec1Parts),
                point: point,
                spki: point && curveOid ? ecSpki(curveOid, point) : null
            };
        }

        if (alg.oid === OID_ED25519 || alg.oid === OID_ED448 || alg.oid === OID_X25519) {
            // The inner OCTET STRING wraps another OCTET STRING holding the seed.
            var seedNode = null;
            try { seedNode = Asn1.parse(inner); } catch (e) { seedNode = null; }
            return {
                kind: Oid.PUBKEY[alg.oid] ? Oid.PUBKEY[alg.oid].kind : 'unknown',
                algorithm: Oid.label(alg.oid), algorithmOid: alg.oid, format: 'pkcs8',
                bits: alg.oid === OID_ED448 ? 448 : 255,
                curve: alg.oid === OID_ED448 ? 'Curve448' : 'Curve25519',
                pkcs8: Asn1.u8(bytes),
                seed: seedNode && seedNode.tag === Asn1.TAG.OCTET_STRING ? seedNode.bytes : null,
                spki: null
            };
        }

        if (alg.oid === OID_DSA) {
            return {
                kind: 'DSA', algorithm: 'DSA', algorithmOid: alg.oid, format: 'pkcs8',
                bits: alg.params && alg.params.children && alg.params.children[0]
                    ? bitLength(alg.params.children[0].bytes) : null,
                curve: null, pkcs8: Asn1.u8(bytes), spki: null
            };
        }

        return {
            kind: 'unknown', algorithm: Oid.label(alg.oid), algorithmOid: alg.oid,
            format: 'pkcs8', bits: null, curve: null, pkcs8: Asn1.u8(bytes), spki: null
        };
    }

    /* ═══ public key from private ═══════════════════════════════════ */

    /**
     * Derive the SPKI. RSA and most EC keys carry enough material to do this
     * arithmetically; an EC key stored without its public point needs a scalar
     * multiplication, which WebCrypto will do for us on the curves it knows.
     * Returns null when neither route is available, so the caller can say why
     * rather than show a wrong answer.
     */
    async function publicFromPrivate(priv) {
        if (priv.spki) return priv.spki;
        if (priv.kind === 'EC' && priv.webcryptoCurve) {
            try {
                var key = await subtle().importKey('pkcs8', bufferOf(priv.pkcs8),
                    { name: 'ECDSA', namedCurve: priv.webcryptoCurve }, true, ['sign']);
                var jwk = await subtle().exportKey('jwk', key);
                var x = b64uToBytes(jwk.x), y = b64uToBytes(jwk.y);
                var point = Asn1.concat([new Uint8Array([0x04]), x, y]);
                priv.point = point;
                priv.spki = ecSpki(priv.curveOid, point);
                return priv.spki;
            } catch (e) { return null; }
        }
        if (priv.kind === 'Ed25519') {
            try {
                var k2 = await subtle().importKey('pkcs8', bufferOf(priv.pkcs8), { name: 'Ed25519' }, true, ['sign']);
                var raw = await subtle().exportKey('spki', k2);
                priv.spki = new Uint8Array(raw);
                return priv.spki;
            } catch (e) { return null; }
        }
        return null;
    }

    function b64uToBytes(s) {
        var t = String(s).replace(/-/g, '+').replace(/_/g, '/');
        while (t.length % 4) t += '=';
        var bin = typeof atob === 'function' ? atob(t) : Buffer.from(t, 'base64').toString('binary');
        var out = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
    }

    function bufferOf(bytes) {
        var b = Asn1.u8(bytes);
        if (b.byteOffset === 0 && b.byteLength === b.buffer.byteLength) return b.buffer;
        // Explicit copy rather than .slice() — see the Buffer note in Asn1.u8.
        var copy = new Uint8Array(b.byteLength);
        copy.set(b);
        return copy.buffer;
    }

    /* ═══ format conversion ═════════════════════════════════════════ */

    function toPkcs8(priv) { return priv.pkcs8; }

    /** "Traditional" format: PKCS#1 for RSA, SEC1 for EC. */
    function toTraditional(priv) {
        if (priv.kind === 'RSA' && priv.pkcs1) return { der: priv.pkcs1, label: 'RSA PRIVATE KEY' };
        if (priv.kind === 'EC' && priv.sec1) return { der: priv.sec1, label: 'EC PRIVATE KEY' };
        return null; // Ed25519 has no traditional form — PKCS#8 is the only one
    }

    /* ═══ PBE decryption (shared with pkcs12.js) ════════════════════ */

    var PKCS12_PBE = {
        '1.2.840.113549.1.12.1.1': { cipher: 'rc4', keyLen: 16, ivLen: 0 },
        '1.2.840.113549.1.12.1.2': { cipher: 'rc4', keyLen: 5, ivLen: 0 },
        '1.2.840.113549.1.12.1.3': { cipher: '3des', keyLen: 24, ivLen: 8 },
        '1.2.840.113549.1.12.1.4': { cipher: '3des', keyLen: 16, ivLen: 8 },
        '1.2.840.113549.1.12.1.5': { cipher: 'rc2', keyLen: 16, ivLen: 8, effectiveBits: 128 },
        '1.2.840.113549.1.12.1.6': { cipher: 'rc2', keyLen: 5, ivLen: 8, effectiveBits: 40 }
    };

    var PRF_HASH = {
        '1.2.840.113549.2.7': 'SHA-1',
        '1.2.840.113549.2.8': 'SHA-224',
        '1.2.840.113549.2.9': 'SHA-256',
        '1.2.840.113549.2.10': 'SHA-384',
        '1.2.840.113549.2.11': 'SHA-512'
    };

    var AES_CBC = {
        '2.16.840.1.101.3.4.1.2': 16,
        '2.16.840.1.101.3.4.1.22': 24,
        '2.16.840.1.101.3.4.1.42': 32
    };

    /**
     * Decrypt a PBE blob given its AlgorithmIdentifier node.
     * Throws Error('WRONG_PASSWORD') when the padding does not check out, and
     * Error('PBE_UNSUPPORTED:<oid>') for anything genuinely unimplemented — the
     * distinction matters because one is the user's fault and one is ours.
     */
    async function decryptPbe(algIdNode, encrypted, password) {
        var alg = readAlgId(algIdNode);
        if (!alg) throw new Error('PBE_NO_ALGID');
        var data = Asn1.u8(encrypted);

        // ── PKCS#12 PBE family (legacy, still dominant in .pfx from Windows)
        if (PKCS12_PBE[alg.oid]) {
            var spec = PKCS12_PBE[alg.oid];
            var params = alg.params;
            var salt = params.children[0].bytes;
            var iters = intOf(params.children[1]);
            var key = Ciphers.pkcs12Kdf(password, salt, 1, iters, spec.keyLen, 'SHA-1');
            var iv = spec.ivLen ? Ciphers.pkcs12Kdf(password, salt, 2, iters, spec.ivLen, 'SHA-1') : null;
            var out;
            if (spec.cipher === '3des') out = Ciphers.tripleDesCbc(key, iv, data, true);
            else if (spec.cipher === 'rc2') out = Ciphers.rc2Cbc(key, iv, data, true, spec.effectiveBits);
            else return Ciphers.rc4(key, data); // stream cipher: no padding to check
            var un = Ciphers.unpadPkcs7(out, 8);
            if (!un) throw new Error('WRONG_PASSWORD');
            return un;
        }

        // ── PBES2 (modern)
        if (alg.oid === '1.2.840.113549.1.5.13') {
            var pbes2 = alg.params;
            var kdf = readAlgId(pbes2.children[0]);
            var scheme = readAlgId(pbes2.children[1]);
            if (kdf.oid !== '1.2.840.113549.1.5.12') throw new Error('PBE_UNSUPPORTED:' + kdf.oid);

            var kp = kdf.params.children;
            var kSalt = kp[0].bytes;
            var kIters = intOf(kp[1]);
            var prf = 'SHA-1', declaredKeyLen = null;
            for (var i = 2; i < kp.length; i++) {
                if (kp[i].tag === Asn1.TAG.INTEGER) declaredKeyLen = intOf(kp[i]);
                else if (kp[i].tag === Asn1.TAG.SEQUENCE && kp[i].children[0]) {
                    prf = PRF_HASH[kp[i].children[0].oid] || 'SHA-1';
                }
            }

            var keyLen = AES_CBC[scheme.oid] || declaredKeyLen ||
                (scheme.oid === '1.2.840.113549.3.7' ? 24 : null);
            if (!keyLen) throw new Error('PBE_UNSUPPORTED:' + scheme.oid);

            var derived = await pbkdf2(password, kSalt, kIters, keyLen, prf);

            if (AES_CBC[scheme.oid]) {
                var aesIv = scheme.params.bytes;
                try {
                    var ck = await subtle().importKey('raw', bufferOf(derived), { name: 'AES-CBC' }, false, ['decrypt']);
                    var plain = await subtle().decrypt({ name: 'AES-CBC', iv: bufferOf(aesIv) }, ck, bufferOf(data));
                    return new Uint8Array(plain);
                } catch (e) {
                    // WebCrypto rejects on bad PKCS#7 padding — that is the
                    // wrong-password signal, not a broken file.
                    throw new Error('WRONG_PASSWORD');
                }
            }
            if (scheme.oid === '1.2.840.113549.3.7') { // des-EDE3-CBC
                var d3 = Ciphers.tripleDesCbc(derived, scheme.params.bytes, data, true);
                var u3 = Ciphers.unpadPkcs7(d3, 8);
                if (!u3) throw new Error('WRONG_PASSWORD');
                return u3;
            }
            throw new Error('PBE_UNSUPPORTED:' + scheme.oid);
        }

        throw new Error('PBE_UNSUPPORTED:' + alg.oid);
    }

    function intOf(node) {
        var b = node.bytes, v = 0;
        for (var i = 0; i < b.length; i++) v = v * 256 + b[i];
        return v;
    }

    async function pbkdf2(password, salt, iterations, keyLen, hash) {
        // Password may arrive as raw bytes (see Ciphers.bmpWithTerminator).
        var pw = password instanceof Uint8Array ? password : new TextEncoder().encode(String(password));
        var base = await subtle().importKey('raw', bufferOf(pw), { name: 'PBKDF2' }, false, ['deriveBits']);
        var bits = await subtle().deriveBits(
            { name: 'PBKDF2', salt: bufferOf(Asn1.u8(salt)), iterations: iterations, hash: hash },
            base, keyLen * 8);
        return new Uint8Array(bits);
    }

    /** Decrypt an EncryptedPrivateKeyInfo DER blob → PKCS#8 bytes. */
    async function decryptPrivateKey(der, password) {
        var node = Asn1.parse(der);
        if (!node.children || node.children.length !== 2) throw new Error('NOT_ENCRYPTED_PKCS8');
        var plain = await decryptPbe(node.children[0], node.children[1].bytes, password);
        // Sanity-check: a right password yields a parseable PKCS#8. A wrong one
        // can survive padding by chance (1 in 256), and this catches that.
        try { Asn1.parse(plain); } catch (e) { throw new Error('WRONG_PASSWORD'); }
        return plain;
    }

    /**
     * Encrypt PKCS#8 with PBES2 / AES-256-CBC / PBKDF2-SHA256.
     * 600 000 iterations follows current OWASP guidance; openssl's own default
     * is far lower, and since we are the ones choosing, we choose the safer one.
     */
    async function encryptPrivateKey(pkcs8, password, iterations) {
        var iters = iterations || 600000;
        var salt = randomBytes(16), iv = randomBytes(16);
        var key = await pbkdf2(password, salt, iters, 32, 'SHA-256');
        var ck = await subtle().importKey('raw', bufferOf(key), { name: 'AES-CBC' }, false, ['encrypt']);
        var ct = await subtle().encrypt({ name: 'AES-CBC', iv: bufferOf(iv) }, ck, bufferOf(pkcs8));

        return Asn1.sequence(
            Asn1.sequence(
                Asn1.oid('1.2.840.113549.1.5.13'),
                Asn1.sequence(
                    Asn1.sequence(
                        Asn1.oid('1.2.840.113549.1.5.12'),
                        Asn1.sequence(
                            Asn1.octetString(salt),
                            Asn1.integerFromNumber(iters),
                            Asn1.sequence(Asn1.oid('1.2.840.113549.2.9'), Asn1.nullValue())
                        )
                    ),
                    Asn1.sequence(Asn1.oid('2.16.840.1.101.3.4.1.42'), Asn1.octetString(iv))
                )
            ),
            Asn1.octetString(new Uint8Array(ct))
        );
    }

    /* ═══ legacy openssl encrypted PEM ══════════════════════════════ */

    var DEK = {
        'AES-128-CBC': { keyLen: 16, aes: true },
        'AES-192-CBC': { keyLen: 24, aes: true },
        'AES-256-CBC': { keyLen: 32, aes: true },
        'DES-EDE3-CBC': { keyLen: 24, aes: false },
        'DES-CBC': { keyLen: 8, aes: false }
    };

    /**
     * Decrypt a "Proc-Type: 4,ENCRYPTED" PEM block. This format predates
     * PKCS#8 and is what an nginx key from 2015 looks like; openssl still
     * reads it, so we must too.
     */
    async function decryptLegacyPem(block, password) {
        var dekInfo = block.headers && block.headers['DEK-Info'];
        if (!dekInfo) throw new Error('NOT_LEGACY_ENCRYPTED');
        var parts = dekInfo.split(',');
        var cipherName = parts[0].trim().toUpperCase();
        var iv = Asn1.fromHex((parts[1] || '').trim());
        var spec = DEK[cipherName];
        if (!spec) throw new Error('PBE_UNSUPPORTED:' + cipherName);

        // The IV doubles as the salt for EVP_BytesToKey — an openssl quirk.
        var derived = Ciphers.evpBytesToKey(password, iv.subarray(0, 8), spec.keyLen, 0);
        var data = block.der;

        if (spec.aes) {
            try {
                var ck = await subtle().importKey('raw', bufferOf(derived.key), { name: 'AES-CBC' }, false, ['decrypt']);
                var plain = await subtle().decrypt({ name: 'AES-CBC', iv: bufferOf(iv) }, ck, bufferOf(data));
                return new Uint8Array(plain);
            } catch (e) { throw new Error('WRONG_PASSWORD'); }
        }
        var out = Ciphers.tripleDesCbc(derived.key, iv, data, true);
        var un = Ciphers.unpadPkcs7(out, 8);
        if (!un) throw new Error('WRONG_PASSWORD');
        return un;
    }

    /* ═══ fingerprints ══════════════════════════════════════════════ */

    async function digest(name, bytes) {
        var d = await subtle().digest(name, bufferOf(bytes));
        return new Uint8Array(d);
    }

    /**
     * The three fingerprints people actually need, computed once:
     *  - sha256/sha1 of the DER — what browsers and `openssl x509 -fingerprint` show
     *  - spkiSha256Base64 — the HPKP / `pin-sha256` value, and the only one that
     *    is stable across a renewal that keeps the key
     */
    async function fingerprints(der, spki) {
        var out = {};
        var s256 = await digest('SHA-256', der);
        var s1 = await digest('SHA-1', der);
        out.sha256 = Asn1.toHex(s256, ':').toUpperCase();
        out.sha1 = Asn1.toHex(s1, ':').toUpperCase();
        out.sha256Plain = Asn1.toHex(s256);
        if (spki) {
            var sp = await digest('SHA-256', spki);
            out.spkiSha256 = Asn1.toHex(sp);
            out.spkiSha256Base64 = (P.Pem || root.PKI.Pem).toBase64(sp);
        }
        return out;
    }

    var API = {
        parsePublic: parsePublic, parsePrivate: parsePrivate,
        publicFromPrivate: publicFromPrivate,
        toPkcs8: toPkcs8, toTraditional: toTraditional,
        decryptPrivateKey: decryptPrivateKey, encryptPrivateKey: encryptPrivateKey,
        decryptLegacyPem: decryptLegacyPem, decryptPbe: decryptPbe,
        fingerprints: fingerprints, digest: digest, pbkdf2: pbkdf2,
        bitLength: bitLength, readAlgId: readAlgId, rsaSpki: rsaSpki, ecSpki: ecSpki,
        randomBytes: randomBytes, bufferOf: bufferOf, subtle: subtle,
        OID_RSA: OID_RSA, OID_EC: OID_EC, OID_ED25519: OID_ED25519
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (root) { root.PKI = root.PKI || {}; root.PKI.Keys = API; }
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
